import { useAuthedUserContext } from '@/clients/shared-hooks/use-authed-user';
import { ENV_VARS } from '@/clients/util/env';
import { UserType } from '@/ifaces/user';
import {
  PropsWithChildren,
  createContext,
  createElement,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { ProfAI } from '../program-app/agents/profai';
import { useLessonScreen } from '../program-app/hooks/use-lesson-screen';
import { Tool } from '../program-app/tools/types';

type PromoContextType = {
  monitor(agent: ProfAI): void;
  promoKey?: string;
  promoLink?: string;
};

const PromoContext = createContext<PromoContextType>({
  monitor: () => {},
  promoKey: undefined,
  promoLink: undefined,
});

export function usePromoContext(agent?: ProfAI) {
  const context = useContext(PromoContext);
  useEffect(() => {
    if (agent) context.monitor(agent);
  }, [agent, context]);
  return context;
}

export const PromoContextProvider = (props: PropsWithChildren) => {
  const auth = useAuthedUserContext();
  const noPromo = auth.user_type !== UserType.Consumer;
  const [agent, monitor] = useState<ProfAI | undefined>(undefined);

  // re-evaluate state changes on tool transitions
  const tool = useLessonScreen(agent);

  // used in hooks to minimize hook dependencies
  const agentRef = useRef(agent);
  agentRef.current = agent;

  const promoKey = useMemo(() => {
    if (!tool || !agentRef.current || tool !== Tool.RESULTS) return '';
    const skillSlug = agentRef.current.getSkill().slug;
    if (!['ai-workflows', 'prompting-fundamentals'].includes(skillSlug)) return '';
    return agentRef.current.getSkill().slug;
  }, [tool]);

  const promoLink = ENV_VARS.VITE_PROMO_LINK;

  const value = {
    promoKey: noPromo ? '' : promoKey,
    promoLink: noPromo ? '' : promoLink,
    monitor,
  };
  return createElement(PromoContext.Provider, { value }, props.children);
};
